import React from 'react';

import { css, cx, keyframes } from '@emotion/css';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Message, ReadReceipt } from '@/models/MessagingModel.ts';
import { userFormattedName } from '@/models/UserModel.ts';
import { Queries } from '@/queries/Queries.ts';
import { Theme } from '@/theme/CreateTheme.ts';

export type MessageReadReceiptProps = {
  message: Message;
  whoHasRead: 'everybody' | 'nobody' | 'some';
  readReceiptList: ReadReceipt[];
  hovering: boolean;
  className?: string;
};

export const MessageReadReceipt: React.FC<MessageReadReceiptProps> = ({
  message,
  whoHasRead,
  readReceiptList,
  hovering,
  className,
}) => {
  const me = Queries.user.usePractitionerMe({});
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  if (message.deleted) {
    return null;
  }

  const readReceiptNames = readReceiptList
    .filter(receipt => receipt.userId !== me.id)
    .map(receipt => userFormattedName(receipt.firstname, receipt.lastname));

  const prefix = readReceiptNames.length > 0 ? t('messaging.readBy') + ' ' : '';

  return (
    <div className={cx(styles.container, className)}>
      {hovering && (
        <Typography variant="bodySmall" className={styles.text}>
          {prefix}
          {readReceiptNames.join(', ')}
        </Typography>
      )}
      {whoHasRead === 'everybody' && (
        <Icons.doubleCheck className={styles.icon} />
      )}
      {whoHasRead === 'some' && <Icons.check className={styles.icon} />}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(4),
  }),
  icon: css({
    justifySelf: 'flex-end',
    width: FontSizes.body,
    height: FontSizes.body,
    color: theme.palette.text.disabled,
  }),
  text: css({
    fontSize: FontSizes.bodySmall,
    color: theme.palette.text.disabled,
    lineHeight: FontSizes.body,
    animation: `${fadeIn} 1s forwards`,
  }),
});

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
